/**
 * Behavior id → presentation lookup.
 *
 * Single source of truth for how a behavior renders client-side. Server
 * pushes ids into Cube.behaviors (via survival/transient.ts mirror); this
 * registry decides what each id looks like in the rich-overlay layer.
 *
 * Adding a new behavior id is one row here + one server-side
 * addTransientBehavior call. The renderer (rich-overlay.ts) does not
 * change. Per memory: feedback_data_driven_unbuilt_deps.
 *
 * Templates:
 *   "strip-icon"    → small icon in the buff strip above the floating bar
 *   "big-countdown" → 48px floating "INCAPACITATED" countdown above hero
 *
 * Hidden ids (server bookkeeping like Mining/Running/Stationary) are
 * declared but skip render. Unregistered ids silently skip — registry
 * miss is non-fatal so a stale client doesn't crash on an unknown id.
 *
 * Spec: docs/superpowers/specs/2026-05-03-floating-overhead-ui-design.md §"Architecture" Layer B
 */

export type BehaviorTemplate = "strip-icon" | "big-countdown";
export type BehaviorGlow = "frost" | "red" | "gold" | "purple" | "none" | "green" | "orange";

export interface BehaviorPresentation {
  /** SVG markup string for strip-icon template; empty for big-countdown. */
  icon: string;
  template: BehaviorTemplate;
  glowColor: BehaviorGlow;
  pulse: boolean;
  /** For big-countdown only: the label rendered above the timer ("INCAPACITATED"). */
  label?: string;
  /** Human-readable name + one-line effect for the buff-strip hover tooltip
   *  (rich-overlay sets icon.title from these). Falls back to the raw id. */
  name?: string;
  desc?: string;
  /** Server-bookkeeping ids that should never render. */
  hidden?: boolean;
}

// Compact 14×14 SVG glyphs. Stroke-only so they pick up CSS color via
// `currentColor` — keeps the registry tech-agnostic about the final
// glow / tint, those come from glowColor + CSS.
const SNOWFLAKE_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<path d="M7 1 L7 13 M2 7 L12 7 M3 3 L11 11 M11 3 L3 11" ` +
  `stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/>` +
  `</svg>`;

const SKULL_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<ellipse cx="7" cy="6" rx="5" ry="4.5" fill="none" stroke="currentColor" stroke-width="1.5"/>` +
  `<rect x="5.5" y="9.5" width="3" height="3" fill="none" stroke="currentColor" stroke-width="1.2"/>` +
  `<circle cx="5" cy="6" r="1" fill="currentColor"/>` +
  `<circle cx="9" cy="6" r="1" fill="currentColor"/>` +
  `</svg>`;

const INFINITY_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<path d="M3 7 C3 5.3 4.3 4 6 4 C7 4 8 5 8 5 L9 6 L10 5 ` +
  `C10 5 11 4 12 4 C13.7 4 15 5.3 15 7 C15 8.7 13.7 10 12 10 ` +
  `C11 10 10 9 10 9 L9 8 L8 9 C8 9 7 10 6 10 C4.3 10 3 8.7 3 7 Z" ` +
  `fill="none" stroke="currentColor" stroke-width="1.4"/>` +
  `</svg>`;

// Weapon-DoT icons (Task 2 item-effects). These keys match BUFF ids from
// `cube.spellbook.buffs` (NOT server transient behaviors) — the floater manager
// merges active weapon-DoT buff ids into the behaviors Map it hands the overlay.
const VENOM_DROP_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<path d="M7 2 C7 2 4 5 4 8 C4 11 7 12 7 12 C7 12 10 11 10 8 C10 5 7 2 7 2Z" ` +
  `fill="none" stroke="currentColor" stroke-width="1.4"/>` +
  `<path d="M7 5 L7 9 M5.5 6.5 L8.5 7.5" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>` +
  `</svg>`;

const FLAME_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<path d="M7 2 C5 4 3 6 3 9 C3 11.2 4.8 12 7 12 C9.2 12 11 11.2 11 9 C11 6 9 4 7 2Z" ` +
  `fill="none" stroke="currentColor" stroke-width="1.4"/>` +
  `<path d="M7 6 C6 7 6 9 7 9 C8 9 8 7 7 6Z" fill="currentColor" stroke="none"/>` +
  `</svg>`;

const BASH_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" stroke-width="1.5"/>` +
  `<path d="M4 4 L10 10 M10 4 L4 10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>` +
  `</svg>`;

// Well-Fed (2026-06-29): drumstick — meat blob + bone. The +10% damage reward.
const DRUMSTICK_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<circle cx="5" cy="5" r="3.2" fill="none" stroke="currentColor" stroke-width="1.5"/>` +
  `<path d="M7 7 L12 12" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>` +
  `</svg>`;

// Exhausted (2026-06-29): downward chevron — winded, walk-speed only (no faint).
const WINDED_SVG =
  `<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">` +
  `<path d="M3 4 L7 8 L11 4 M3 8 L7 12 L11 8" fill="none" stroke="currentColor" ` +
  `stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>` +
  `</svg>`;

const HIDDEN_PRESENTATION: BehaviorPresentation = {
  icon: "",
  template: "strip-icon",
  glowColor: "none",
  pulse: false,
  hidden: true,
};

const BEHAVIORS: Record<string, BehaviorPresentation> = {
  // Survival edge states (visible)
  Frostbite: {
    icon: SNOWFLAKE_SVG,
    template: "strip-icon",
    glowColor: "frost",
    pulse: true,
    name: "Frostbite",
    desc: "Freezing — losing health and can't run. Get to a fire.",
  },
  Starving: {
    icon: SKULL_SVG,
    template: "strip-icon",
    glowColor: "red",
    pulse: true,
  },
  Unconscious: {
    icon: "",
    template: "big-countdown",
    glowColor: "red",
    pulse: true,
    label: "INCAPACITATED",
  },
  Sated: {
    icon: INFINITY_SVG,
    template: "strip-icon",
    glowColor: "frost",
    pulse: false,
    // Dormant — Cooking L3 perk lights it up when expertise lands
  },
  // Well-Fed (hunger ≥ 50) — pure-positive buff: +10% outgoing damage (2026-06-29).
  WellFed: {
    icon: DRUMSTICK_SVG,
    template: "strip-icon",
    glowColor: "gold",
    pulse: false,
    name: "Well-Fed",
    desc: "+10% attack damage while fed (hunger 50+).",
  },
  // Exhausted (0 stamina) — walk-speed only until recovered; no faint (2026-06-29).
  Exhausted: {
    icon: WINDED_SVG,
    template: "strip-icon",
    glowColor: "orange",
    pulse: false,
    name: "Exhausted",
    desc: "Out of stamina — walk only until you recover.",
  },

  // Weapon DoT debuffs (Task 2) — keyed by BUFF id (cube.spellbook.buffs).
  NoxiousVenom:           { icon: VENOM_DROP_SVG, template: "strip-icon", glowColor: "green",  pulse: true },
  VenomousArbalestPoison: { icon: VENOM_DROP_SVG, template: "strip-icon", glowColor: "green",  pulse: true },
  UndyingFlames:          { icon: FLAME_SVG,      template: "strip-icon", glowColor: "orange", pulse: true },
  FelFlames:              { icon: FLAME_SVG,      template: "strip-icon", glowColor: "red",    pulse: true },
  RollingPinLifePact:     { icon: BASH_SVG,       template: "strip-icon", glowColor: "gold",   pulse: true },

  // Server bookkeeping (hidden)
  Mining: HIDDEN_PRESENTATION,
  Running: HIDDEN_PRESENTATION,
  Stationary: HIDDEN_PRESENTATION,
  WalkToggled: HIDDEN_PRESENTATION,
  Heat: HIDDEN_PRESENTATION,
  Resting: HIDDEN_PRESENTATION,
};

/**
 * Resolve a behavior id to its presentation. Returns null for unknown ids
 * (silently skipped by the renderer — non-fatal if a server pushes an id
 * the client doesn't recognize, e.g., during a deploy mismatch).
 */
export function lookupBehavior(id: string): BehaviorPresentation | null {
  return BEHAVIORS[id] ?? null;
}
